/**
 * ProtectedRoute
 *
 * Guards routes that require:
 *   a) Authentication  — redirects to /login if not authenticated, preserving
 *      the intended URL in location state so LoginPage can redirect back.
 *   b) Minimum role   — when requiredRole is supplied, redirects to /dashboard
 *      if the authenticated user's role is below the required level.
 *
 * Loading state: renders a full-page spinner while the session is being
 * restored to avoid a flash of the login page on every hard refresh.
 */
import { Navigate, useLocation } from 'react-router-dom'
import { type ReactNode } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ROUTES } from '@/constants/routes'
import type { UserRole } from '@/types'

interface ProtectedRouteProps {
  children: ReactNode
  /**
   * Minimum role required to access this route.
   * Omit to allow any authenticated user regardless of role.
   */
  requiredRole?: UserRole
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasPermission } = useAuth()
  const location = useLocation()

  // ── Still restoring session — don't flash login page ───────────────────
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Restoring session…" />
      </div>
    )
  }

  // ── Not authenticated — redirect to login, remember intended path ───────
  if (!isAuthenticated) {
    return (
      <Navigate
        to={ROUTES.AUTH.LOGIN}
        state={{ from: location }}
        replace
      />
    )
  }

  // ── Authenticated but insufficient role — redirect to dashboard ─────────
  if (requiredRole && !hasPermission(requiredRole)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />
  }

  return <>{children}</>
}
